"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import { Loader2, Mail, ArrowLeft, ArrowRight } from "lucide-react";
import { useAction } from "next-safe-action/hooks";
import { toast } from "sonner";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";

import { requestPasswordReset } from "@/features/auth/actions/reset-password";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

function createRequestResetSchema(t: (key: string) => string) {
  return z.object({
    email: z.string().email(t("validation.invalidEmail")),
  });
}

type RequestResetData = z.infer<ReturnType<typeof createRequestResetSchema>>;

const entrance = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0 },
};

export function ResetPasswordForm() {
  const t = useTranslations("auth.resetPassword");
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const requestResetSchema = createRequestResetSchema(t);

  const form = useForm<RequestResetData>({
    resolver: zodResolver(requestResetSchema),
    defaultValues: {
      email: "",
    },
  });

  const { execute, isPending } = useAction(requestPasswordReset, {
    onSuccess: () => {
      setSubmittedEmail(form.getValues("email"));
      setSubmitted(true);
    },
    onError: () => {
      // Always show success for security
      setSubmittedEmail(form.getValues("email"));
      setSubmitted(true);
    },
  });

  function onSubmit(data: RequestResetData) {
    execute(data);
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-6 text-center"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Mail className="h-7 w-7 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-display font-bold text-foreground tracking-tight">
            {t("checkEmailTitle")}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("checkEmailMessage", { email: submittedEmail })}
          </p>
        </div>
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary/80 transition-colors"
        >
          {t("returnToSignIn")}
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="show"
      transition={{ staggerChildren: 0.06 }}
      className="space-y-8"
    >
      {/* Accent + Heading */}
      <motion.div variants={entrance} className="space-y-3">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
          <div className="w-6 h-[2px] bg-primary/30 rounded-full" />
        </div>
        <h2 className="text-3xl font-display font-bold text-foreground tracking-tight">
          {t("title")}
        </h2>
        <p className="text-[15px] text-muted-foreground">
          {t("subtitle")}
        </p>
      </motion.div>

      {/* Form */}
      <motion.div variants={entrance}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">{t("emailLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t("emailPlaceholder")}
                      autoComplete="email"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full h-11 text-sm font-semibold tracking-wide"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("sendingResetLink")}
                </>
              ) : (
                <>
                  {t("sendResetLink")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </Form>
      </motion.div>

      {/* Back link */}
      <motion.div variants={entrance} className="text-center">
        <Link
          href="/login"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("backToSignIn")}
        </Link>
      </motion.div>
    </motion.div>
  );
}
